/**
 * Type checking utility functions
 *
 * Provides helper functions to check the type of values and properties in objects
 */

/**
 * Gets the type of a value as a string
 * @param value - The value to check
 * @returns A string representing the type of the value
 */
export const getValueType = (value: unknown): string => {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (Array.isArray(value)) return "array";
  return typeof value;
};

/**
 * Gets the type of a property in an object
 * @param obj - The object to check
 * @param key - The key of the property to check
 * @returns A string representing the type of the property or "undefined" if the property doesn't exist
 */
export const getPropertyType = <T extends object>(obj: T, key: keyof T | string): string => {
  // Check if the key exists in the object
  if (!(key in obj)) return "undefined";

  // Get the value and return its type
  const value = obj[key as keyof T];
  return getValueType(value);
};

/**
 * Checks if a property exists in an object
 * @param obj - The object to check
 * @param key - The key of the property to check
 * @returns Boolean indicating if the property exists and is not null or undefined
 */
export const hasProperty = <T extends object>(obj: T, key: keyof T | string): boolean => {
  return key in obj && obj[key as keyof T] !== null && obj[key as keyof T] !== undefined;
};
